import PropTypes from 'prop-types';
import { getApiFileUrl } from '../../../core/utils/fileUtils';
import './primitives.css';

/**
 * PersonAvatar
 *
 * The leading tile for a *person*, where IconTile leads a thing. A circle, not
 * a rounded square (`docs/design-system.md` § radius — `full` is for avatars),
 * so a roster reads as people at a glance and never gets confused with the
 * course, room and lesson rows sitting next to it in the same register.
 *
 * A photo when the user has one; otherwise the two-letter monogram that stood
 * in before photos existed, on the same 12% tint the icon tile uses. The
 * fallback is not a generic silhouette on purpose: 40 identical grey heads in a
 * list carry less than 40 distinct monograms.
 *
 * Give it `onOpen` and it becomes the profile control — the photo and the name
 * beside it are the two things that open a public profile, and the row is not.
 */
const initialsOf = (person = {}) => {
  const a = (person.firstName || person.fullName || person.name || person.username || '').trim()[0] || '';
  const b = (person.lastName || '').trim()[0]
    || ((person.fullName || person.name || '').trim().split(/\s+/)[1] || '').trim()[0]
    || '';
  return (a + b).toUpperCase() || '?';
};

const PersonAvatar = ({
  person,
  photoUrl,
  size = 'sm',
  onOpen,
  label,
  className = '',
}) => {
  const src = getApiFileUrl(photoUrl ?? person?.profilePhotoUrl);
  const initials = initialsOf(person);
  const interactive = typeof onOpen === 'function';

  const classes = [
    'lms-person-avatar',
    `lms-person-avatar--${size}`,
    interactive ? 'lms-person-avatar--interactive' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const content = src ? (
    <img src={src} alt="" loading="lazy" />
  ) : (
    <span className="lms-person-avatar__initials" aria-hidden="true">
      {initials}
    </span>
  );

  if (!interactive) {
    return (
      <span className={classes} role={label ? 'img' : undefined} aria-label={label || undefined} aria-hidden={label ? undefined : true}>
        {content}
      </span>
    );
  }

  return (
    <button
      type="button"
      className={classes}
      aria-label={label}
      onClick={(event) => {
        event.stopPropagation();
        onOpen(event);
      }}
    >
      {content}
    </button>
  );
};

PersonAvatar.propTypes = {
  /** The user record — read for `profilePhotoUrl` and for the monogram fallback. */
  person: PropTypes.object,
  /** Overrides `person.profilePhotoUrl` when the photo lives on another field. */
  photoUrl: PropTypes.string,
  /** sm 32px (register rows), md 40px (record heads, drawers), lg 56px (identity). */
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  /** Opens the public profile. Given, the avatar becomes a button. */
  onOpen: PropTypes.func,
  /** Required when interactive — names whose profile the control opens. */
  label: PropTypes.string,
  className: PropTypes.string,
};

export { initialsOf };
export default PersonAvatar;
